import { useEffect } from "react";

import type { components } from "../../api/schema";
import { parseTranscriptTimelineSearch } from "../../lib/routerSearch";
import type { ListQueryResult } from "../../lib/useListQuery";

type Answer = components["schemas"]["AnswerResponse"];
type MarkerRevision = components["schemas"]["MarkerRevisionResponse"];

export function labelSeedKey(
  label: string | undefined,
  markerId: string | undefined,
  answer: Answer | null,
): string {
  if (label === "new") {
    return `new:${markerId ?? "none"}`;
  }
  return label == null
    ? "closed"
    : `answer:${label}:${answer ? "ready" : "loading"}`;
}

export function usePageToEnd<T>(
  enabled: boolean,
  list: ListQueryResult<T>,
): void {
  useEffect(() => {
    if (
      enabled &&
      list.errorMessage == null &&
      list.hasNextPage &&
      !list.isFetching
    ) {
      list.fetchNextPage();
    }
  }, [enabled, list]);
}

export function useRevisionPagination(
  revisionId: string | undefined,
  revisions: ListQueryResult<MarkerRevision>,
): void {
  const hasRevision = revisions.items.some((item) => item.id === revisionId);
  useEffect(() => {
    if (
      revisionId != null &&
      !hasRevision &&
      revisions.errorMessage == null &&
      revisions.hasNextPage &&
      !revisions.isFetching
    ) {
      revisions.fetchNextPage();
    }
  }, [hasRevision, revisionId, revisions]);
}

interface RouteLocation {
  pathname: string;
  search: object;
}

// A dirty Label blocks leaving the transcript, the expanded row, the feed, or
// the working scope — never a same-row search change (a panel, a span).
export function labelLeaveBlocks({
  current,
  next,
}: {
  current: RouteLocation;
  next: RouteLocation;
}): boolean {
  const currentSearch: Record<string, unknown> = { ...current.search };
  const nextSearch: Record<string, unknown> = { ...next.search };
  const from = parseTranscriptTimelineSearch(currentSearch);
  const to = parseTranscriptTimelineSearch(nextSearch);
  return (
    current.pathname !== next.pathname ||
    from.label !== to.label ||
    currentSearch.agent !== nextSearch.agent ||
    currentSearch.team !== nextSearch.team ||
    from.feed !== to.feed
  );
}
